import React, { createRef, PureComponent } from 'react';
import { Flex } from '../../common/Flex';
import Icon, {
    CheckOutlined,
    ClockCircleFilled,
    CloseCircleFilled,
    EyeOutlined,
    HddOutlined,
    MessageFilled,
    MessageOutlined,
    PlusCircleOutlined,
    SearchOutlined,
    SettingFilled,
    StopOutlined,
    SyncOutlined,
} from '@ant-design/icons';
import TimeUtils from '../../../common/util/TimeUtils';
import { CountType, RegulationProposalAction, TimePeriod } from 'app/model/CORE';
import { Badge, Button, Divider, notification, Switch, Tooltip } from 'antd';
import { SelectionLabel } from '../../common/SelectionLabel';
import {
    CoordinationState,
    CoordinationSummary,
    ExtendedMonitoredTv,
    MonitoredMcdmMeasure,
    SolutionState,
    TVTimePeriodSelection,
} from 'app/model/SOL49';
import KeyUtils from '../../../common/util/KeyUtils';
import { TextFilterInput } from 'app/common/input/TextFilterInput';
import { ModalHandler } from '../../panels/ModalHandler';
import './NetworkMonitoringHeader.less';
import MeasureUtils from 'app/common/util/MeasureUtils';
import { McdmMeasureNotification } from 'app/components/panels/coordination/McdmMeasureNotification';
import {
    MonitoringPeriodSettingsChange,
    OpenMcdmMeasure,
    ShowModalEvent,
    UpdateMcdmMeasureProposals,
} from 'app/common/eventbus/Events';
import AppContext from 'app/common/context/AppContext';
import Utils from 'app/common/util/Utils';
import TimePeriodUtils from '../../../common/util/TimePeriodUtils';
import { MeasureProposalFilterSettings } from '../detailed/measures/MeasureProposalFilter';
import ConditionalRender from '../../../common/components/ConditionalRender';
import { CoordinationSummaryNotification } from '../../panels/coordination/CoordinationSummaryNotification';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { MenuUtils } from 'app/components/dashboard/common/MenuUtils';
import { CoordinationSummariesPanel } from '../../panels/coordination/CoordinationSummariesPanel';
import AuthorisationUtils from 'app/common/context/AuthorisationUtils';
import GlobalTimeSelectionPopup from 'app/components/dashboard/network/popup/GlobalTimeSelectionPopup';
import { CoordinationUtils, SolutionsStatusMap } from 'app/common/util/CoordinationUtils';
import { UnorderedListOutlined } from '@ant-design/icons/lib';
import { EarthIcon } from 'app/components/icons/EarthIcon';
import { MonitoringPeriodSettings, TimeOptionEnum } from 'app/model/client/SOL49';
import { SoundUtils } from './SoundUtils';
import { MonitoredDaySelector } from 'app/components/dashboard/network/MonitoredDaySelector';
import { RootState } from 'app/redux/store';
import { connect, ConnectedProps } from 'react-redux';
import {
    WholeNetworkSettings,
    WholeNetworkSettingsContent,
    WholeNetworkSettingsTitle,
} from 'app/components/dashboard/network/flows/settings/WholeNetworkSettings';
import { ClosablePopover } from 'app/components/common/ClosablePopover';
import { InitialNetworkPlanPanel } from 'app/components/panels/inp/InitialNetworkPlanPanel';
import INP_LOGO from 'app/components/dashboard/network/inp.png';
import { setDrawerOpen, setFlowSettings } from 'app/redux/features/dashboard/dashboard-slice';
import AppConstants from 'app/common/context/AppConstants';

import { ApplicableScenarioMenuUtils, ScenarioMenuState } from '../common/ApplicableScenarioMenuUtils';
import {
    addToFlowSection,
    createFlowSection,
    selectUserCustomFlows,
} from '../../../redux/features/dashboard/user-monitored-flows-slice';

const mapStateToProps = (state: RootState) =>
    Object.assign({}, state.groupedFlowState, {
        newChatMessages: state.dashboardData.newChatMessages,
        drawerOpen: state.dashboardData.drawerOpen,
        dashboardData: state.dashboardData.dashboardData,
        textFilter: state.dashboardData.globalTextFilter,
        filteredTvSets: state.dashboardData.filteredTvSets,
        filteredTvs: state.dashboardData.filteredTvs,
        nbHiddenTvs: state.dashboardData.nbHiddenTvs,
        nbMonitorableTvs: state.dashboardData.nbMonitorableTvs,
        nbLoadedTvs: state.dashboardData.nbLoadedTvs,
        nbSupervisedAndRegulated: state.dashboardData.nbSupervisedAndRegulated,
        settings: state.dashboardData.settings,
        customFlowSections: selectUserCustomFlows(state),
    });
const mapDispatchToProps = { setFlowSettings, setDrawerOpen, createFlowSection, addToFlowSection };

const connector = connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true });
type PropsFromRedux = ConnectedProps<typeof connector>;

type NetworkMonitoringHeaderProps = PropsFromRedux & {
    monitoredPeriod: TimePeriod;
    currentTime: Date;
    selectedTvs?: Array<ExtendedMonitoredTv>;
    tvSetMap?: { [key: string]: string };

    showMap?: boolean; // if undefined, don't show button
    onShowMapChanged?(showMap: boolean): void;
    fullscreenMap?: boolean; // if undefined don't show options button
    onFullscreenMapChanged?(fullscreenMap: boolean): void;

    countType?: CountType; // if undefined, don't show button
    onCountTypeChanged?(countType: CountType): void;

    hideTextFilter?: boolean;
    onTextFilterChange?(value: string): void;

    onSelectAll?: () => void;
    onClearSelection?: () => void;

    onGlobalFilterEnabledChanged(filterEnabled: boolean): void;
    globalFilterEnabled: boolean;

    monitoredMcdmMeasures: Array<MonitoredMcdmMeasure>;
    coordinationSummaries: Array<CoordinationSummary>;
};

interface NetworkMonitoringHeaderState extends ScenarioMenuState {
    notifiedCoordinationStates: { [id: string]: SolutionsStatusMap };
    lastConsultedCoordinationStates: { [id: string]: SolutionsStatusMap };
    coordinationMessagesRead: Set<string>;
    notifiedUnreadMessages: Set<string>;
    revertToNotShowMap?: boolean;
    timePeriodDisplayMode: TimeOptionEnum;
}

export class NetworkMonitoringHeader extends PureComponent<NetworkMonitoringHeaderProps, NetworkMonitoringHeaderState> {
    // @ts-ignore
    private textFilterInputRef = createRef<Input>();
    private globalTimeSelectionPopupRef = createRef<GlobalTimeSelectionPopup>();
    private settingsPopoverRef = React.createRef<ClosablePopover>();

    constructor(props: NetworkMonitoringHeaderProps) {
        super(props);
        this.state = {
            notifiedCoordinationStates: {},
            lastConsultedCoordinationStates: {},
            coordinationMessagesRead: new Set<string>(),
            notifiedUnreadMessages: new Set<string>(),
            timePeriodDisplayMode: TimeOptionEnum.RELATIVE_TO_NOW,
            isApplicableScenarioLoading: false,
        };
    }

    componentDidMount() {
        document.addEventListener('keydown', this.handleKeydown);
        document.addEventListener('mousedown', this.handleClick);

        AppContext.eventBus.register(OpenMcdmMeasure.NAME, (event: OpenMcdmMeasure) => {
            this.handleOpenSolution(event.measures);
        });

        // Prevent notifications at startup
        if (this.props.coordinationSummaries) {
            this.updateNotifiedCoordinationStates();
        }
    }

    componentDidUpdate(prevProps: Readonly<NetworkMonitoringHeaderProps>) {
        // Since we update the state in here, we need to make sure that this is not triggered again for the same measures
        if (Utils.isNotShallowEquals(this.props.monitoredMcdmMeasures, prevProps.monitoredMcdmMeasures)) {
            // This will keep the mcdm measure panel (if it's open) up-to-date
            UpdateMcdmMeasureProposals.fireEvent(this.props.monitoredMcdmMeasures);

            this.displayMcdmMeasuresNotifications(this.props.monitoredMcdmMeasures, prevProps.monitoredMcdmMeasures);
        }

        if (Utils.isNotShallowEquals(this.props.coordinationSummaries, prevProps.coordinationSummaries)) {
            this.displayCoordinationSummaries(this.props.coordinationSummaries, prevProps.coordinationSummaries);
            // Reset the coordination read messages to sync with server data
            this.setState({
                coordinationMessagesRead: new Set<string>(),
            });
        }

        if (Utils.isNotShallowEquals(this.props.selectedTvs, prevProps.selectedTvs)) {
            this.setState({
                isApplicableScenarioLoading: false,
                applicableScenarios: null,
                requestedScenarioTp: null,
            });
        }
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeydown);
        document.removeEventListener('mousedown', this.handleClick);
    }

    updateNotifiedCoordinationStates = () => {
        const newNotifiedUnreadMessages = new Set<string>(this.state.notifiedUnreadMessages);
        const newCache = {};
        this.props.coordinationSummaries?.forEach(summary => {
            newCache[summary.id] = CoordinationUtils.getCoordinationStatusMap(summary);
            if (summary.unreadMessage) {
                newNotifiedUnreadMessages.add(summary.id);
            }
        });
        this.setState({
            notifiedCoordinationStates: newCache,
            notifiedUnreadMessages: newNotifiedUnreadMessages,
        });
    };

    displayMcdmMeasuresNotifications = (
        monitoredMcdmMeasures: Array<MonitoredMcdmMeasure>,
        prevMonitoredMeasures: Array<MonitoredMcdmMeasure>,
    ) => {
        const [newMeasures, updatedMeasures]: [MonitoredMcdmMeasure[], MonitoredMcdmMeasure[]] =
            monitoredMcdmMeasures.reduce(
                (result, current) => {
                    const previous = prevMonitoredMeasures.find(
                        prev => prev.measure.measureId.id === current.measure.measureId.id,
                    );
                    if (Utils.isNotExists(previous) && MeasureUtils.isMcdmStateActive(current.mcdmMeasureTopic.state)) {
                        // New mcdm measure
                        result[0].push(current);
                    } else if (
                        Utils.isExists(previous) &&
                        (previous.mcdmMeasureTopic.state !== current.mcdmMeasureTopic.state ||
                            MeasureUtils.getNMOCApprovalState(previous.mcdmMeasureTopic) !==
                                MeasureUtils.getNMOCApprovalState(current.mcdmMeasureTopic))
                    ) {
                        // Measure's state has been updated (by NMOC or TACT)
                        result[1].push(current);
                    }
                    return result;
                },
                [[], []],
            );

        if (Utils.isNotEmpty(newMeasures) && AuthorisationUtils.isNmUser()) {
            // TODO We only display one "new" measure notification
            this.showProposalNotification(newMeasures[0]);
        }
        if (Utils.isNotEmpty(updatedMeasures) && AuthorisationUtils.isFmpUser()) {
            // Display all the updates, since its FMP dependent
            updatedMeasures.forEach(m => this.showProposalNotification(m));
        }
    };

    displayCoordinationSummaries = (
        coordinationSummaries: CoordinationSummary[],
        prevSummaries: CoordinationSummary[],
    ) => {
        const [updatedCoordination, newCoordinations]: [CoordinationSummary[], CoordinationSummary[]] =
            coordinationSummaries.reduce(
                (result, current) => {
                    if (prevSummaries.find(prev => prev.id === current.id)) {
                        result[0].push(current);
                    } else {
                        result[1].push(current);
                    }
                    return result;
                },
                [[], []],
            );

        if (Utils.isNotEmpty(newCoordinations)) {
            AuthorisationUtils.isFmpUser() && this.showCoordinationNotification(newCoordinations[0]);
        }
        if (Utils.isNotEmpty(updatedCoordination)) {
            updatedCoordination.forEach(summary => {
                if (summary.state === CoordinationState.ONGOING) {
                    const diffs = CoordinationUtils.getCoordinationStateUpdateDiff(
                        summary,
                        this.state.notifiedCoordinationStates[summary.id],
                    );
                    const unreadMessage = summary.unreadMessage && !this.state.notifiedUnreadMessages.has(summary.id);
                    if (Object.keys(diffs).length > 0 || unreadMessage) {
                        this.showCoordinationNotification(summary, unreadMessage, diffs);
                    }
                } else if (prevSummaries.find(prevSummary => prevSummary.id === summary.id).state !== summary.state) {
                    AuthorisationUtils.isFmpUser() && this.showCoordinationNotification(summary);
                }
            });
        }

        this.updateNotifiedCoordinationStates();
    };

    handleKeydown = event => {
        if (event.ctrlKey && (event.key === 'F' || event.key === 'f')) {
            // CTRL+F
            event.preventDefault();
            this.textFilterInputRef.current?.focus();
        }
    };

    handleClick = event => {
        if (event.ctrlKey && event.button === 1) {
            // CTRL + middle click
            event.preventDefault();
            this.props.countType &&
                this.props.onCountTypeChanged?.(
                    this.props.countType === CountType.ENTRY ? CountType.OCCUPANCY : CountType.ENTRY,
                );
        }
    };

    handleTextFilterChange = (textFilter: string) => {
        this.props.onTextFilterChange?.('' + textFilter);
    };

    handleFindSolution = () => {
        const tvs: Array<TVTimePeriodSelection> = this.props.selectedTvs.map(monitoredTv => ({
            tvId: monitoredTv.refLocation.id,
        }));
        ModalHandler.showFindSolution(tvs);
    };

    handleMonitoringFlexClick = () => {
        this.globalTimeSelectionPopupRef.current.show();
    };

    handleMonitoringPeriodSettingsChange = (monitoringPeriodSettings: MonitoringPeriodSettings) => {
        MonitoringPeriodSettingsChange.fireEvent(monitoringPeriodSettings);
    };

    private handleOpenSolution = (measures: MonitoredMcdmMeasure[]) => {
        // If there are multiple measures with same tv, create an unique TvWithTimePeriod that covers all
        const tvsWithTimePeriod = new Map<string, TVTimePeriodSelection>();
        measures
            .map(m => m.measure)
            .forEach(measure => {
                const tvId = MeasureUtils.getTvLocation(measure).id;
                if (!tvsWithTimePeriod.has(tvId)) {
                    tvsWithTimePeriod.set(tvId, { tvId: tvId, timePeriod: measure.applicability, selected: true });
                } else {
                    const prevTimePeriod = tvsWithTimePeriod.get(tvId).timePeriod;
                    const newTimePeriod = TimePeriodUtils.coversAll([prevTimePeriod, measure.applicability]);
                    tvsWithTimePeriod.set(tvId, { tvId: tvId, timePeriod: newTimePeriod, selected: true });
                }
            });

        return ModalHandler.showFindSolution(Array.from(tvsWithTimePeriod.values()), measures);
    };

    getSelectionLabelMenuItems = (): ItemType[] => {
        const selectedTvIds = this.props.selectedTvs.map(monitoredTv => monitoredTv.refLocation.id);
        return [
            { key: 'find-solution', onClick: this.handleFindSolution, label: 'Find solution' },
            ApplicableScenarioMenuUtils.applicableScenarioMenu(
                this.props.selectedTvs.map(monitoredTv => monitoredTv.refLocation.id),
                null,
                this.state,
                state => {
                    this.setState(state);
                },
            ),
            { key: 'divider-1', type: 'divider' },
            MenuUtils.addToCustomSectionMenu(
                this.props.customFlowSections,
                payload => this.props.addToFlowSection(payload),
                payload => this.props.createFlowSection(payload),
                selectedTvIds,
            ),
        ];
    };

    private showProposalNotification = (monitoredMcdmMeasure: MonitoredMcdmMeasure) => {
        notification.close(monitoredMcdmMeasure.measure.measureId.id);
        notification.open({
            key: monitoredMcdmMeasure.measure.measureId.id,
            message: <McdmMeasureNotification monitoredMcdmMeasure={monitoredMcdmMeasure} />,
            duration: 10,
            placement: 'topLeft',
            className: 'notifications proposals',
            closeIcon: <CloseCircleFilled />,
        });
        SoundUtils.playNotificationSound();
    };

    private showCoordinationNotification = (
        c: CoordinationSummary,
        unreadMessage?: boolean,
        diffs?: SolutionsStatusMap,
    ) => {
        // TODO handle has more
        notification.close(c.id);
        notification.open({
            key: c.id,
            message: (
                <CoordinationSummaryNotification
                    coordinationSummary={c}
                    diff={diffs}
                    newMessage={unreadMessage}
                    onOpenCoordination={this.updateLastConsultedState}
                />
            ),
            duration: 10,
            placement: 'topRight',
            className: 'notifications coordination',
            closeIcon: <CloseCircleFilled />,
        });
        SoundUtils.playNotificationSound();
    };

    private updateLastConsultedState = (summary: CoordinationSummary) => {
        const coordinationStates = CoordinationUtils.getCoordinationStatusMap(summary);
        const updatedCache = Object.assign({}, this.state.lastConsultedCoordinationStates);
        updatedCache[summary.id] = coordinationStates;
        const newNotificationsCache = Object.assign({}, this.state.notifiedCoordinationStates);
        newNotificationsCache[summary.id] = coordinationStates;
        const messagesRead = new Set<string>(this.state.coordinationMessagesRead);
        messagesRead.add(summary.id);
        const newNotifiedUnreadMessages = new Set<string>(this.state.notifiedUnreadMessages);
        newNotifiedUnreadMessages.delete(summary.id);
        this.setState({
            lastConsultedCoordinationStates: updatedCache,
            notifiedCoordinationStates: newNotificationsCache,
            coordinationMessagesRead: messagesRead,
            notifiedUnreadMessages: newNotifiedUnreadMessages,
        });
    };

    private handleShowProposals = (tvFilter: string, actions: RegulationProposalAction[]) => {
        const filterSettings: MeasureProposalFilterSettings = { actions, activeOnly: true, tvFilter };
        ModalHandler.showMcdmMeasures(this.props.monitoredMcdmMeasures, filterSettings);
    };

    private hasNewMcdmMeasure = (mcdmMeasures: MonitoredMcdmMeasure[]): boolean => {
        // TODO highlight if state changed ? only concerns FMP dashboard..
        return mcdmMeasures?.some(MeasureUtils.isNewMcdmMeasure);
    };

    renderMeasureProposals = () => {
        if (!this.props.monitoredMcdmMeasures) return;

        const proposedMeasures = this.props.monitoredMcdmMeasures.filter(mcdmMeasure =>
            MeasureUtils.isMcdmStateActive(mcdmMeasure.mcdmMeasureTopic.state),
        );
        const createProposals = proposedMeasures?.filter(
            mcdmMeasure =>
                (MeasureUtils.isRegulationProposal(mcdmMeasure.measure) &&
                    mcdmMeasure.measure.action === RegulationProposalAction.CREATION) ||
                MeasureUtils.isRerouting(mcdmMeasure.measure),
        );
        const updateProposals = proposedMeasures?.filter(
            mcdmMeasure =>
                MeasureUtils.isRegulationProposal(mcdmMeasure.measure) &&
                mcdmMeasure.measure.action === RegulationProposalAction.UPDATE,
        );
        const cancelProposals = proposedMeasures?.filter(
            mcdmMeasure =>
                MeasureUtils.isRegulationProposal(mcdmMeasure.measure) &&
                mcdmMeasure.measure.action === RegulationProposalAction.CANCELLATION,
        );

        return (
            <>
                <Flex direction="vertical" grow={false} className="measure-proposals">
                    <div>MEASURE PROPOSALS</div>
                    <Flex direction="horizontal" grow={false} className="buttons">
                        <Tooltip
                            title="Creation"
                            overlayInnerStyle={{ minHeight: '0' }}
                            overlayStyle={{ paddingBottom: '0' }}
                            showArrow={false}>
                            <Button
                                className={this.hasNewMcdmMeasure(createProposals) ? 'highlighted' : ''}
                                onClick={() => this.handleShowProposals('', [RegulationProposalAction.CREATION])}>
                                <span>{createProposals?.length || 0}</span>
                                <PlusCircleOutlined />
                            </Button>
                        </Tooltip>
                        <Tooltip
                            title="Update"
                            overlayInnerStyle={{ minHeight: '0' }}
                            overlayStyle={{ paddingBottom: '0' }}
                            showArrow={false}>
                            <Button
                                className={this.hasNewMcdmMeasure(updateProposals) ? 'highlighted' : ''}
                                onClick={() => this.handleShowProposals('', [RegulationProposalAction.UPDATE])}>
                                <span>{updateProposals?.length || 0}</span>
                                <SyncOutlined />
                            </Button>
                        </Tooltip>
                        <Tooltip
                            title="Cancellation"
                            overlayInnerStyle={{ minHeight: '0' }}
                            overlayStyle={{ paddingBottom: '0' }}
                            showArrow={false}>
                            <Button
                                className={this.hasNewMcdmMeasure(cancelProposals) ? 'highlighted' : ''}
                                onClick={() => this.handleShowProposals('', [RegulationProposalAction.CANCELLATION])}>
                                <span>{cancelProposals?.length || 0}</span>
                                <StopOutlined />
                            </Button>
                        </Tooltip>
                    </Flex>
                </Flex>
                <Divider type="vertical" className="divider" />
            </>
        );
    };

    getMappedCoordinations = (
        coordinations: Array<CoordinationSummary>,
    ): [CoordinationSummary[], CoordinationSummary[], CoordinationSummary[]] => {
        return coordinations.reduce(
            ([timedOut, onGoing, terminated], coordination) => {
                if (coordination.state !== CoordinationState.ONGOING) {
                    terminated.push(coordination);
                } else if (TimeUtils.compare(coordination.targetTime, AppContext.getCurrentTime()) < 0) {
                    timedOut.push(coordination);
                } else {
                    onGoing.push(coordination);
                }
                return [timedOut, onGoing, terminated];
            },
            [[], [], []],
        );
    };

    hasNewCoordination = (coordinations: Array<CoordinationSummary>) => {
        if (AuthorisationUtils.isNmUser()) return false;
        return coordinations.some(coordination => {
            const solutionStates = coordination.status[AuthorisationUtils.getFmpUnit()];
            const states: SolutionState[] = Object.values(solutionStates);
            return states.every(state => state === SolutionState.NO_RESPONSE);
        });
    };

    hasUpdate = (coordinations: Array<CoordinationSummary>) => {
        return coordinations.some(c => {
            const isNewForNm =
                AuthorisationUtils.isNmUser() && Utils.isNotExists(this.state.lastConsultedCoordinationStates[c.id]);
            const diffs = CoordinationUtils.getCoordinationStateUpdateDiff(
                c,
                this.state.lastConsultedCoordinationStates[c.id],
            );
            const newStateUpdate = c.solutions.some(
                s =>
                    diffs[s.name] &&
                    Object.keys(diffs[s.name]).filter(state => state !== SolutionState.NO_RESPONSE).length > 0,
            );
            const unreadMessage = !this.state.coordinationMessagesRead.has(c.id) && c.unreadMessage;
            return !isNewForNm && (unreadMessage || newStateUpdate);
        });
    };

    renderCoordinations = () => {
        if (!this.props.coordinationSummaries) return;

        const summaries = this.props.coordinationSummaries;
        const [timedOut, onGoing, terminated] = this.getMappedCoordinations(summaries);
        return (
            <>
                <Divider type="vertical" className="divider" />
                <Flex direction="vertical" className="coordination">
                    <div>COORDINATION</div>
                    <Flex direction="horizontal" grow={false} className="buttons">
                        <Tooltip
                            title="Timed-Out"
                            overlayInnerStyle={{ minHeight: '0' }}
                            overlayStyle={{ paddingBottom: '0' }}
                            showArrow={false}>
                            <Button
                                onClick={() => this.openCoordinationSummariesPanel(timedOut)}
                                className={timedOut.length > 0 ? 'timed-out' : ''}>
                                <span>{timedOut.length}</span>
                                <ClockCircleFilled />
                            </Button>
                        </Tooltip>
                        <Tooltip
                            title="Ongoing"
                            overlayInnerStyle={{ minHeight: '0' }}
                            overlayStyle={{ paddingBottom: '0' }}
                            showArrow={false}>
                            <Button
                                onClick={() => this.openCoordinationSummariesPanel(onGoing)}
                                className={
                                    onGoing.length > 0 && (this.hasUpdate(onGoing) || this.hasNewCoordination(onGoing))
                                        ? 'highlighted'
                                        : ''
                                }>
                                <span>{onGoing.length}</span>
                                <MessageFilled />
                            </Button>
                        </Tooltip>
                        <Tooltip
                            title="Finished"
                            overlayInnerStyle={{ minHeight: '0' }}
                            overlayStyle={{ paddingBottom: '0' }}
                            showArrow={false}>
                            <Button
                                onClick={() => this.openCoordinationSummariesPanel(terminated)}
                                className={
                                    terminated.length > 0 &&
                                    AuthorisationUtils.isFmpUser() &&
                                    this.hasUpdate(terminated)
                                        ? 'highlighted'
                                        : ''
                                }>
                                <span>{terminated.length}</span>
                                <CheckOutlined />
                            </Button>
                        </Tooltip>
                    </Flex>
                </Flex>
            </>
        );
    };

    openCoordinationSummariesPanel = (coordinationSummaries: CoordinationSummary[]) => {
        Object.keys(this.state.notifiedCoordinationStates).forEach(id => notification.close(id));
        const unreadMessages = {};
        coordinationSummaries.forEach(
            c => (unreadMessages[c.id] = c.unreadMessage && !this.state.coordinationMessagesRead.has(c.id)),
        );
        const content = (
            <CoordinationSummariesPanel
                coordinationSummaries={coordinationSummaries}
                statusCache={this.state.lastConsultedCoordinationStates}
                onOpenCoordination={this.updateLastConsultedState}
                unreadMessages={unreadMessages}
            />
        );
        AppContext.eventBus.publish(new ShowModalEvent(ShowModalEvent.NAME, content, '63em', '98em', '6em'));
    };

    //this is handling the weird request: "Switching from “Network Monitoring” to “Map monitoring” should automatically turn on the “Map” switch. Switching back to “Networking Monitoring” should turn back the “Map” switch to its original state. "
    switchFullscreenMap = () => {
        this.props.onFullscreenMapChanged(!this.props.fullscreenMap);
        if (!this.props.fullscreenMap && !this.props.showMap) {
            this.handleShowMapChanged(true);
            this.setState({
                revertToNotShowMap: true,
            });
        }
        if (this.state.revertToNotShowMap) {
            this.handleShowMapChanged(false);
            this.setState({
                revertToNotShowMap: false,
            });
        }
    };

    handleShowMapChanged = (checked: boolean) => {
        this.props.onShowMapChanged?.(checked);
    };

    handleMonitoredDayChange = (deltaDay: number) => {
        this.handleMonitoringPeriodSettingsChange({ ...AppContext.monitoringPeriodSettings, deltaDay });
    };

    handleSettingsChange = (settings: WholeNetworkSettings) => {
        this.updateSettings(settings);
        this.settingsPopoverRef.current.hide();
    };

    updateSettings = (settings: WholeNetworkSettings) => {
        this.props.setFlowSettings(settings);
    };

    handleSearch = () => {
        const searchString = Utils.isEmptyString(this.props.textFilter)
            ? ''
            : this.props.textFilter.toUpperCase() + '*';
        ModalHandler.showQueryPanel(searchString);
    };

    handleChatIconClick = () => {
        this.props.setDrawerOpen(!this.props.drawerOpen);
    };

    networkSettingsHint = () => {
        return (
            <Flex direction="vertical" style={{ fontSize: '1.1em', lineHeight: '1.1em' }}>
                <Flex>{`${this.props.nbHiddenTvs} TFV are hidden, displaying only:`}</Flex>
                <Flex direction="vertical" style={{ paddingLeft: '1em' }}>
                    <ConditionalRender condition={this.props.settings.tv !== 'all'}>
                        <span>{`- ${this.props.settings.tv} (${this.props.nbSupervisedAndRegulated})`}</span>
                    </ConditionalRender>
                    <ConditionalRender condition={this.props.settings.load !== 'all'}>
                        <span>{`- ${this.props.settings.load} ${this.props.settings.load === 'overload' ? '' : 'threshold'} (${this.props.nbLoadedTvs})`}</span>
                    </ConditionalRender>
                    <ConditionalRender condition={!this.props.settings.showNonMonitorableTv}>
                        <span>{`- monitorable (${this.props.nbMonitorableTvs})`}</span>
                    </ConditionalRender>
                </Flex>
            </Flex>
        );
    };

    render() {
        const nbTvSelected = this.props.selectedTvs?.length || 0;
        const selectedClusters =
            this.props.tvSetMap &&
            this.props.selectedTvs?.map(tv => this.props.tvSetMap[KeyUtils.MONITORED_TV_KEY_PROVIDER.getKey(tv)]);
        const nbSelectedClusters = new Set(selectedClusters).size;

        const selectionLabelTitle = `${nbTvSelected} TV${nbTvSelected > 1 ? 's' : ''} selected`;
        const selectionLabelSubTitle =
            nbSelectedClusters > 0
                ? `${nbSelectedClusters} cluster${nbSelectedClusters > 1 ? 's' : ''} selected`
                : undefined;

        const selectionMenuItems = this.getSelectionLabelMenuItems();
        return (
            <Flex className="network-monitoring-header">
                <Flex direction={'vertical'} size="23%" style={{ alignItems: 'flex-start' }}>
                    {' '}
                    {/* Center top left */}
                    <SelectionLabel
                        visible={nbTvSelected > 0}
                        title={selectionLabelTitle}
                        subTitle={selectionLabelSubTitle}
                        onSelectAll={this.props.onSelectAll}
                        onClearSelection={this.props.onClearSelection}
                        extraMenuItems={selectionMenuItems}
                        style={{ marginTop: '0.5em' }}
                    />
                    <Button style={{ position: 'absolute', top: '3.5em', marginLeft: '0.5em' }}>
                        <Icon
                            title={'Initial Network Plan'}
                            component={() => <img src={INP_LOGO} alt={'INP'} style={{ height: '2em' }} />}
                            onClick={() =>
                                ShowModalEvent.fireNotClosable(
                                    <InitialNetworkPlanPanel />,
                                    InitialNetworkPlanPanel.WIDTH + 'em',
                                )
                            }
                        />
                    </Button>
                    <Button
                        style={{
                            position: 'absolute',
                            top: '3.5em',
                            marginLeft: '4.5em',
                            width: '3.7em',
                            height: '2.7em',
                        }}
                        onClick={() => ModalHandler.showAllApplicableScenarios()}>
                        <HddOutlined style={{ fontSize: '1.6em' }} />
                    </Button>
                    <ConditionalRender condition={!AppConstants.hideChatDrawer}>
                        <Button
                            style={{
                                position: 'absolute',
                                top: '3.5em',
                                marginLeft: '8.5em',
                                width: '3.7em',
                                height: '2.7em',
                            }}
                            onClick={this.handleChatIconClick}>
                            <Icon
                                title={'Chat'}
                                component={() => (
                                    <Badge
                                        title="New messages"
                                        offset={[10, -10]}
                                        count={this.props.newChatMessages > 0 ? this.props.newChatMessages : undefined}>
                                        <MessageOutlined
                                            style={{
                                                fontSize: '1.5em',
                                                color: this.props.newChatMessages > 0 ? '#60B1CC' : '#8C8B82',
                                            }}
                                        />
                                    </Badge>
                                )}
                            />
                        </Button>
                    </ConditionalRender>
                </Flex>
                <Flex size="15%" style={{ justifyContent: 'flex-end' }}>
                    <ConditionalRender condition={AppContext.isFmpCoordinationEnabled()}>
                        {this.renderMeasureProposals()}
                    </ConditionalRender>
                </Flex>
                <Flex size="20%" direction="vertical">
                    <GlobalTimeSelectionPopup
                        ref={this.globalTimeSelectionPopupRef}
                        onApply={this.handleMonitoringPeriodSettingsChange}>
                        <Flex direction="vertical" className="time-and-date" onClick={this.handleMonitoringFlexClick}>
                            <div style={{ marginTop: '0.25vw' }}>
                                {TimeUtils.formatDateDDMMYYYY(this.props.currentTime)}
                            </div>
                            <div style={{ marginTop: '0.5em' }}>
                                <span style={{ marginRight: '0.3em' }}>
                                    {TimeUtils.formatHHMM(this.props.monitoredPeriod?.from)}
                                </span>
                                <span style={{ fontSize: '2em' }}>{TimeUtils.formatHHMM(this.props.currentTime)}</span>
                                <span style={{ marginLeft: '0.3em' }}>
                                    {TimeUtils.formatHHMM(this.props.monitoredPeriod?.to)}
                                </span>
                            </div>
                        </Flex>
                    </GlobalTimeSelectionPopup>
                    <Flex style={{ justifyContent: 'center', marginTop: '0.5em' }}>
                        <MonitoredDaySelector
                            dataset={AppContext.getDataset().type}
                            value={TimeUtils.diffInDays(
                                AppContext.getMonitoredDay(),
                                TimeUtils.withoutTimeOfDay(TimeUtils.NOW()),
                            )}
                            onChange={this.handleMonitoredDayChange}
                        />
                    </Flex>
                </Flex>
                <Flex size="15%">
                    <ConditionalRender condition={AppContext.isFmpCoordinationEnabled()}>
                        {this.renderCoordinations()}
                    </ConditionalRender>
                </Flex>
                <Flex
                    grow={false}
                    direction="vertical"
                    style={{ justifyContent: 'center', position: 'relative', marginLeft: 'auto', marginTop: '0.5em' }}>
                    <Flex style={{ alignItems: 'center', gap: '0.5em' }}>
                        <ConditionalRender condition={Utils.isExists(this.props.fullscreenMap)}>
                            <Flex style={{ fontSize: '1.5em' }} grow={false}>
                                <ConditionalRender condition={!this.props.fullscreenMap}>
                                    <UnorderedListOutlined title={'TVs + Map'} onClick={this.switchFullscreenMap} />
                                </ConditionalRender>
                                <ConditionalRender condition={this.props.fullscreenMap}>
                                    <EarthIcon
                                        className="icon"
                                        title={'Fullscreen Map'}
                                        onClick={this.switchFullscreenMap}
                                    />
                                </ConditionalRender>
                            </Flex>
                        </ConditionalRender>
                        <ConditionalRender condition={Utils.isExists(this.props.showMap)}>
                            <Switch
                                style={{ width: '5em' }}
                                unCheckedChildren={'MAP'}
                                checkedChildren={'MAP'}
                                checked={this.props.showMap}
                                onChange={this.handleShowMapChanged}
                            />
                        </ConditionalRender>
                        <ConditionalRender condition={Utils.isExists(this.props.countType)}>
                            <Switch
                                style={{ width: '8em' }}
                                unCheckedChildren={'ENTRY'}
                                checkedChildren={'OCCUPANCY'}
                                checked={this.props.countType === CountType.OCCUPANCY}
                                onChange={checked =>
                                    this.props.onCountTypeChanged?.(checked ? CountType.OCCUPANCY : CountType.ENTRY)
                                }
                            />
                        </ConditionalRender>
                        <Switch
                            style={{ width: '8em' }}
                            unCheckedChildren={'FILTER OFF'}
                            checkedChildren={'FILTER ON'}
                            checked={this.props.globalFilterEnabled}
                            onChange={checked => this.props.onGlobalFilterEnabledChanged(checked)}
                        />
                    </Flex>
                    <Flex style={{ alignItems: 'center', gap: '0.5em' }}>
                        <ConditionalRender condition={!this.props.hideTextFilter}>
                            <Flex style={{ alignItems: 'center', gap: '0.5em' }}>
                                <SearchOutlined
                                    title={'Query'}
                                    style={{ fontSize: '1.5em' }}
                                    onClick={this.handleSearch}
                                />
                                <TextFilterInput
                                    disabled={!this.props.globalFilterEnabled}
                                    hidePrefix={true}
                                    value={this.props.textFilter}
                                    onTextFilterChange={this.handleTextFilterChange}
                                    inputRef={this.textFilterInputRef}
                                />
                            </Flex>
                        </ConditionalRender>
                    </Flex>
                    <ConditionalRender condition={!this.props.hideTextFilter}>
                        <Flex
                            direction={'vertical'}
                            style={{ position: 'absolute', top: '5em', gap: '0.5em', zIndex: 10 }}>
                            <ConditionalRender condition={!this.props.fullscreenMap && this.props.nbHiddenTvs > 0}>
                                <Flex
                                    style={{
                                        backgroundColor: '#3A3A3A',
                                        color: '#EEEEEE',
                                        padding: '0.5em',
                                        borderRadius: '0.5em',
                                        gap: '1em',
                                        fontSize: '1.2em',
                                    }}>
                                    {this.networkSettingsHint()}
                                    <EyeOutlined
                                        title="Show ALL"
                                        onClick={() =>
                                            this.updateSettings({
                                                ...this.props.settings,
                                                load: 'all',
                                                showNonMonitorableTv: true,
                                                tv: 'all',
                                            })
                                        }
                                    />
                                    <ClosablePopover
                                        key="settings-action"
                                        ref={this.settingsPopoverRef}
                                        trigger="click"
                                        placement="bottom"
                                        title={<WholeNetworkSettingsTitle />}
                                        content={
                                            <WholeNetworkSettingsContent
                                                settings={this.props.settings}
                                                onChange={this.handleSettingsChange}
                                                onClose={() => this.settingsPopoverRef.current.hide()}
                                            />
                                        }>
                                        <SettingFilled onClick={() => this.settingsPopoverRef.current.show()} />
                                    </ClosablePopover>
                                </Flex>
                            </ConditionalRender>
                        </Flex>
                    </ConditionalRender>
                </Flex>
            </Flex>
        );
    }
}

export default connector(NetworkMonitoringHeader);
